import { datadogLogs } from "@datadog/browser-logs";
import { isGraphqlEndpoint } from "@/utils/observability/util";
import {
  parseFetchRequestBody,
  parseFetchRequestInput,
} from "./parseFetchBody";

const SPAN_TAG_LIMIT = 900;

export interface DatadogAdditionalData {
  request?: {
    url: string;
    method?: string;
    headers?: Record<string, string> | undefined;
    body?: unknown;
    operationName?: string;
    variables?: Record<string, unknown>;
    query?: string;
    parseError?: unknown;
  };
  response?: {
    headers?: Record<string, string>;
    ok?: boolean;
    redirected?: boolean;
    status?: number;
    statusText?: string;
    type?: ResponseType;
    url?: string;
    data?: unknown;
    parseError?: unknown;
  };
  error?: unknown;
  parseError?: unknown;
}

interface GraphqlBody {
  operationName: string;
  variables: Record<string, string>;
  query: string;
}

export const extractFetchHeaders = (
  headers?: HeadersInit
): Record<string, string> | undefined => {
  if (!headers) {
    return undefined;
  }
  const headersMap = {};
  const headersIterable =
    Array.isArray(headers) || headers instanceof Headers
      ? headers
      : Object.entries(headers);

  for (const [key, value] of headersIterable) {
    if (key.toLowerCase() === "authorization") {
      continue;
    }
    headersMap[key] = value;
  }

  return headersMap;
};

const handleParseError = (parseError: unknown) => {
  if (parseError instanceof Error) {
    datadogLogs.logger.warn(
      "There was error with extracting request data from this fetch request to track in datadog",
      null,
      parseError
    );
  } else {
    datadogLogs.logger.warn(
      "There was error with extracting request data from this fetch request to track in datadog",
      null,
      new Error(`UnknownError ${parseError}`)
    );
  }
};

const extractFetchRequestData = async (
  input: URL | RequestInfo,
  init?: RequestInit
) => {
  try {
    if (typeof input === "string" || input instanceof URL) {
      const method = init?.method ?? "GET";
      const headers = extractFetchHeaders(init?.headers);
      const body = await parseFetchRequestBody(init?.body, headers);

      return { method, headers, body };
    }
    const method = input.method;
    const headers = extractFetchHeaders(input.headers);
    const body = await parseFetchRequestInput(input.clone(), headers);

    return { method, headers, body };
  } catch (parseError: unknown) {
    handleParseError(parseError);

    return {
      parseError,
    };
  }
};

async function parseResponse(
  response?: Response,
  options?: { skipBody?: boolean }
): Promise<DatadogAdditionalData["response"]> {
  if (!response) {
    return undefined;
  }

  try {
    let data: unknown = undefined;

    // we shouldn't have response with bodyUsed here,
    // but calling clone() on response with bodyUsed would throw an error
    if (!response.bodyUsed && options?.skipBody !== true) {
      data = await parseResponseData(response);
    }

    return {
      headers: extractFetchHeaders(response.headers),
      ok: response.ok,
      redirected: response.redirected,
      status: response.status,
      statusText: response.statusText,
      type: response.type,
      url: response.url,
      data,
    };
  } catch (parseError) {
    handleParseError(parseError);

    return {
      parseError,
    };
  }
}

const parseResponseData = async (response: Response) => {
  // don't track clerk responses which might contain jwt token
  if (response.url.includes(process.env.NEXT_PUBLIC_CLERK_HOSTNAME)) {
    return "REDACTED";
  }
  if (
    response.headers
      .get("content-type")
      ?.toLowerCase()
      .includes("application/json")
  ) {
    return response.clone().json();
  }

  return response.clone().text();
};

const isIntrospectionQuery = (operationName: string) => {
  return operationName === "IntrospectionQuery";
};

const getRequestUrl = (input: URL | RequestInfo) => {
  if (typeof input === "string") {
    return input;
  }

  if (input instanceof URL) {
    return input.toString();
  }

  return input.url;
};

/**
 * Shallow clone error to avoid referencing same error in additionalDatadogData
 *
 * error = { additionalDatadogData: { error }}
 *   ^                                   ^
 *   |                                   |
 *   |-----------------------------------|---- same error without cloning
 */
function shallowCloneError(original?: Record<string, unknown>) {
  if (!original) {
    return undefined;
  }

  if (original instanceof Error) {
    const cloned = new (original.constructor as ErrorConstructor)(
      original.message
    );
    cloned.name = original.name;
    if (original.stack) {
      cloned.stack = original.stack;
    }
    for (const prop of Object.getOwnPropertyNames(original)) {
      if (["name", "message", "stack"].includes(prop)) {
        continue;
      }
      cloned[prop] = original[prop];
    }

    return cloned;
  }

  return { ...original };
}

export const extractFetchData = async (
  input: URL | RequestInfo,
  init: RequestInit | undefined,
  { response, error }: { response?: Response; error?: Record<string, unknown> }
): Promise<DatadogAdditionalData | undefined> => {
  try {
    const url = getRequestUrl(input);
    if (url.includes(process.env.NEXT_PUBLIC_DATADOG_SITE)) {
      return undefined;
    }

    const requestData = await extractFetchRequestData(input, init);
    const requestDataKeysCount = countInnermostKeys(requestData);

    if (isGraphqlEndpoint(input)) {
      const gqlBody = requestData.body as GraphqlBody | undefined;
      const operationName = gqlBody?.operationName;
      const parsedResponse = await extractFetchResponseData(
        response,
        requestDataKeysCount,
        { skipBody: isIntrospectionQuery(operationName) }
      );

      return {
        request: {
          url,
          operationName,
          variables: gqlBody?.variables,
          query: gqlBody?.query,
          ...requestData,
        },
        response: parsedResponse,
        error: shallowCloneError(error),
      };
    }

    const parsedResponse = await extractFetchResponseData(
      response,
      requestDataKeysCount
    );

    return {
      request: {
        url,
        ...requestData,
      },
      response: parsedResponse,
      error: shallowCloneError(error),
    };
  } catch (parseError) {
    handleParseError(parseError);

    return {
      parseError,
    };
  }
};

/**
 * Recursively counts only the innermost keys in a nested object or elements in a nested array.
 * Handles circular references by using a WeakSet to track visited objects.
 *
 * @param {any} obj - The input object or array to count the keys from. Can be a nested structure.
 * @param {WeakSet} [visited] - A WeakSet instance used to track visited objects to avoid circular reference issues.
 * @return {number} The total count of innermost keys in the nested structure. Returns Infinity for circular references.
 */
export function countInnermostKeys(obj: any, visited = new WeakSet()): number {
  if (obj === null) return 0;
  if (typeof obj !== "object") return 1;

  // Prevent circular reference infinite loops
  if (visited.has(obj)) return Infinity;
  visited.add(obj);
  let count = 0;

  if (Array.isArray(obj)) {
    for (const item of obj) {
      count += countInnermostKeys(item, visited);
    }
  } else {
    for (const key in obj) {
      if (Object.hasOwn(obj, key)) {
        const value = obj[key];
        if (value !== null && typeof value === "object") {
          count += countInnermostKeys(value, visited);
        } else {
          count++; // Only count if no nested objects found
        }
      }
    }
  }

  return count;
}

/**
 * Extract data from Response object. Body will be serialized as string in case property count would exceed Datadog limits.
 **/
async function extractFetchResponseData(
  response: Response | undefined,
  requestDataKeysCount: number,
  options?: { skipBody?: boolean }
): Promise<DatadogAdditionalData["response"]> {
  const parsedResponse = await parseResponse(response, options);
  const responseKeysCount = countInnermostKeys(parsedResponse);

  if (requestDataKeysCount + responseKeysCount > SPAN_TAG_LIMIT) {
    try {
      parsedResponse.data = JSON.stringify(parsedResponse.data);
    } catch (stringifyError) {
      datadogLogs.logger.warn(
        "There was error stringifying large response data, dropping..",
        null,
        stringifyError
      );
      parsedResponse.data = "";
    }
  }

  return parsedResponse;
}
